import { ReportSettingsFormValue } from '../../../shared/forms/ReportSettingsForm/ReportSettingsForm';
import { MovementTypeCode } from '../../../config/constants';

export type ReportFormat = {
  id: string | number;
  type: string;
  ext: string;
};

export interface ReportRequest {
  type: string;
  name?: string;
  from: Date;
  to: Date;
  excludedGroups?: number[];
  format?: ReportFormat;
  includedIngredients?: string[];
  includedRations?: string[];
  includedGroups?: string[];
}

export interface ReportInput {
  from: Date;
  to: Date;
}

interface ReportGroup {
  name: string;
  items: DMReportItem[];
}

export interface GroupSummary<T> {
  key: string;
  items: T[];
}

export interface ReportGridData<T> {
  groupingColumn?: string;
  summaryItems?: WithSummaryDetails<T>[];
  groupSummaries?: GroupSummary<T>[];
  items: T[];
  groupItems?: ReportGroup[];
  description?: string;
}

type WithSummaryDetails<T> = T & {
  stock?: number;
  plannedDailyUsage?: number;
  actualDailyUsage?: number;
  projectedRequirements?: number;
  additionalStock?: number;
  projectedCosts?: number;
};

export interface BatchGridItem {
  batchId: string;
  description: string;
  batchDate: string;
}

export interface LoadGridItem {
  batchId: string;
  loadDateTime?: string;
  ingredientName?: string;
  callWeight: number;
  dryCallWeight: number;
  loadedWeight: number;
  dryLoadedWeight: number;
  dryMatterPerc: number;
  deviation: number;
  deviationPerc: number;
  pricePlanned: number;
  priceActual: number;
  priceError: number;
  driverName?: string;
  _isMedication?: boolean;
  _errorClass?: number;
}

export interface DeliveryGridItem {
  batchId: string;
  deliverDateTime?: string;
  groupName?: string;
  startingPreset: number;
  callWeight?: number;
  deliveredWeight: number;
  deviation: number;
  deviationPerc: number;
  pricePlanned: number;
  priceActual: number;
  priceError: number;
  driverName?: string;
  _errorClass?: number;
}

export interface DryMatterEfficiencyItem {
  feedingDate: string;
  dryAmount: number;
  dryRefusal: number;
  concentrate: number;
  headCount: number;
  dmIntake: number;
  milkAmount: number;
  milkPrice: number;
  milkRevenue: number;
  feedCost: number;
  refusalCredit: number;
  concentrateCost: number;
  totalCost: number;
  costPerMilk: number;
  marginPerCow: number;
  fatPerc: number;
  proteinPerc: number;
  dmEfficiency: number;
  dmEfficiencyCM: number;
  _trendClass: number;
}

export interface ReportData<T> {
  lastDelivery?: string;
  gridData: ReportGridData<T>;
  batchGridData?: ReportGridData<BatchGridItem>;
  loadGridData?: ReportGridData<LoadGridItem>;
  groupSummaries?: ReportGridData<T>;
  deliveryGridData?: ReportGridData<DeliveryGridItem>;
  driverGridData?: ReportGridData<OperatorAccuracyReportDriverItem>;
  groupGridData?: ReportGridData<T>;
  detailGridDataGroup?: ReportGridData<T>;
  detailGridDataLot?: ReportGridData<T>;
  feedCosts?: number;
  otherCosts?: number;
  totalCosts?: number;
}

export interface ReportViewSettings {
  pageOrientation: number;
  collapsed: boolean;
}

interface UserSettings {
  currencyString: string;
  dateFormat: string;
  decimalSeparator: string;
  language: string;
  thousandSeparator: string;
  timeFormat: string;
  timeZone: string;
  volumeUnit: string;
  weightUnit: string;
}

export enum SortingOrder {
  ASCENDING = 1,
  DESCENDING = 2,
}

export enum GroupingMode {
  AUTOMATIC = 0,
  GROUP = 1,
  LOT = 2,
}

export interface Report<T> {
  farmName: string;
  userTopconId: string;
  timeStamp: string;
  title: string;
  subTitle: string;
  reportData: ReportData<T>;
  viewSettings: ReportViewSettings;
  userSettings: UserSettings;
  dataSettings: {
    sortingColumn?: string;
    sortingOrder?: SortingOrder;
  };
}

export interface DMReportItem {
  groupLotId?: string;
  rationNames: string;
  groupLotName?: string;
  deliveredDate?: string;
  checked?: boolean;
  headCount?: number;
  callWeight?: number;
  callWeightChange?: number;
  deliveredWeight?: number;
  dryDeliveredWeight?: number;
  priceActual?: number;
  priceActualPerCow?: number;
  refusal?: number;
  refusalPerc?: number;
  intakePerCow?: number;
  _trendClass?: number;
}

export interface DMReportGroupedItem extends ReportGridData<DMReportItem> {
  groupedItems: DMReportItem[][];
}

export interface IngredientUsageReportItem {
  ingredientName: string;
  actualCallWeight: number;
  actualLoadedWeight: number;
  dryCallWeight: number;
  dryLoadedWeight: number;
  deviation?: number;
  weightUnit: string;
  errorPerc?: number;
  pricePlanned: number;
  priceActual: number;
  priceError: number;
  _isPremix?: boolean;
  _errorClass?: number;
}

export interface LoadReportItem {
  batchId: string;
  loadDateTime: Date;
  ingredientName: string;
  callWeight: number;
  dryCallWeight: number;
  loadedWeight: number;
  dryLoadedWeight: number;
  dryMatterPerc: number;
  deviation: number;
  deviationPerc: number;
  pricePlanned: number;
  priceActual: number;
  priceError: number;
  driverName: string;
  description?: string;
  batchDate?: string;
  startingPreset?: string;
  deliveredWeight?: string;
}

export interface LoadReportData {
  items: LoadReportItem[];
  loadData: LoadReportItem[];
  deliveryData: LoadReportItem[];
  loadDataSummary: LoadReportItem;
  deliveryDataSummary: LoadReportItem;
  description?: string;
  batchDate?: string;
  batchId: string;
}
export interface CattleMovementReportItem {
  lotId: string;
  lotDescription: string;
  timestamp: string;
  movementType: string;
  headCount: number;
  fromGroupName?: string;
  toGroupName?: string;
  avgWeight?: number;
}

export interface ClosedLotsReportItem {
  lotId: string;
  lotDescription: string;
  customerId: string;
  customerName: string;
  startDateTime: string;
  closeOutDateTime: string;
}
export interface CattleMovementReportData {
  farmName: string;
  subTitle: string;
  tenantId: string;
  title: string;
  userSettings: UserSettings;
  viewSettings: ReportViewSettings;
  warning: string;
  reportData: { items: CattleMovementReportItem[] };
}

export interface OperatorAccuracyReportItem {
  driverId?: string;
  groupName?: string;
  batchDate?: string;
  rationName: string;
  headCount: number;
  loadError: number;
  loadAccuracy: number;
  deliveryError: number;
  deliveryAccuracy: number;
  overallError: number;
  overallAccuracy: number;
  _loadErrorClass?: number;
  _deliveryErrorClass?: number;
  _overallErrorClass?: number;
}

export interface OperatorAccuracyReportDriverItem {
  driverId: string;
  driverName: string;
  groups?: OperatorAccuracyReportItem[][];
  summary?: OperatorAccuracyReportItem;
}

export interface GroupDeliveryItem {
  groupId: string;
  groupName: string;
  deliveredDate?: string;
  checked: boolean;
  headCount: number;
  callWeight: number;
  deliveredWeight: number;
  dryDeliveredWeight: number;
  deviation: number;
  errorPerc: number;
  accuracyPerc: number;
  pricePlanned: number;
  priceActual: number;
  priceError: number;
  _trendClass?: number;
  _errorClass?: number;
}

export interface GroupDeliveryGroupedItem {
  groupName: string;
  items: GroupDeliveryItem[];
  summary: GroupDeliveryItem[];
}

export interface ReportSettings {
  maxTrend?: number;
  errorTolerance?: number;
  showInsideTolerance?: boolean;
  showOutsideTolerance?: boolean;
  showDeliveries?: boolean;
  showLoads?: boolean;
  movementTypes?: MovementTypeCode[];
  includedGroups?: string[];
  includedGroupTypes?: string[];
  priceUnitCode?: number;
  showDisplayNames?: boolean;
  usePlannedUsage?: string;
  filterMode?: 1 | 2;
  dataSettings?: {
    sortingColumn?: string;
    sortingOrder?: SortingOrder;
    daysBack?: number;
    showBatchNo?: boolean;
  };
  viewSettings?: {
    groupingMode?: GroupingMode;
    showDmeCM?: boolean;
    showFatProtein?: boolean;
    collapsed?: boolean | string;
    pageOrientation?: number;
    showDryMatterPerc?: false;
  };
}

export interface ReportFilterSettingsItem {
  id: string;
  itemName: string;
  premix?: boolean;
  deleted?: boolean;
  notInUse?: boolean;
  disabled?: boolean;
}

export interface ReportFilterSettings {
  groups?: ReportFilterSettingsItem[];
  groupTypes?: ReportFilterSettingsItem[];
  ingredients?: ReportFilterSettingsItem[];
  ingredientTypes?: ReportFilterSettingsItem[];
  rations?: ReportFilterSettingsItem[];
  drivers?: ReportFilterSettingsItem[];
  mixers?: ReportFilterSettingsItem[];
  lots?: ReportFilterSettingsItem[];
  lotTypes?: ReportFilterSettingsItem[];
  relations?: ReportFilterSettingsItem[];
  stockTransactionTypes?: ReportFilterSettingsItem[];
}

export interface RationDeliveryItem {
  groupLotId: string;
  groupLotName?: string;
  deliveredDate?: string;
  checked?: boolean;
  rationName: string;
  headCount: number;
  callWeight: number;
  dryCallWeight: number;
  deliveredWeight: number;
  dryDeliveredWeight: number;
  deviation: number;
  errorPerc: number;
  pricePlanned: number;
  priceActual: number;
  priceError: number;
  driverName?: string;
  _trendClass?: number;
  _errorClass?: number;
}

export interface RationDeliveryGroupedItem {
  groupName: string;
  items: RationDeliveryItem[];
  summary?: RationDeliveryItem[];
}

export interface IngredientUsageByPenItem {
  groupLotId: string;
  groupLotName: string;
  ingredientId: string;
  ingredientName: string;
  actWeight: number;
  dryWeight: number;
  weightUnit: string;
  actWeightHead: number;
  dryWeightHead: number;
  weightHeadUnit: string;
  price: number;
  priceHead: number;
  headCount: number;
}

export interface InventoryItem {
  ingredientId: string;
  ingredientName: string;
  stock: number;
  maxStock: number;
  dryStock: number;
  dryMatterPerc: number;
  stockPerc: number;
  averageDailyUsage: number;
  stockDaysLeft: number;
  expectedOutOfStock: string;
  priceUnit: string;
  price: number;
  totalPrice: number;
  orderDate?: string;
  _alertDaysMin1: number;
  _alertDaysMin2: number;
  _alertPercsMin1: number;
  _alertPercsMin2: number;
}

export interface IngredientUsageByPenGroupedItem {
  items: IngredientUsageByPenItem[];
  summary: IngredientUsageByPenItem;
  groupLotId: string;
  groupLotName: string;
}

export interface PenSettingChangesItem {
  timeStamp: Date;
  changeType: number;
  changeTypeName: string;
  changeSource: string;
  userId: string;
  userName: string;
  groupId: string;
  groupName: string;
  groupFieldNames: string;
  groupFieldDescriptions: string[];
  groupOldValues: string[];
  groupNewValues: string[];
  feedingNumber: number;
  feedingName: string;
  feedingRationId: number;
  feedingRationName: string;
  feedingFieldNames: string;
  feedingFieldDescriptions: string[];
  feedingOldValues: string[];
  feedingNewValues: string[];
}

export interface RationSettingChangesItem {
  timeStamp: Date;
  changeType: number;
  changeTypeName: string;
  changeSource: string;
  userId: string;
  userName: string;
  rationId: string;
  groupId: string;
  rationName: string;
  rationFieldNames: string;
  rationFieldDescriptions: string[];
  rationOldValues: string[];
  rationNewValues: string[];
  ingredientName: string;
  ingredientDescripion: string;
  ingredientFieldNames: string;
  ingredientFieldDescriptions: string[];
  ingredientOldValues: string[];
  ingredientNewValues: string[];
  ingredientAmountDescription: string;
}

export interface IngredientSettingChangesItem {
  timeStamp: Date;
  changeType: number;
  changeTypeName: string;
  changeSource: string;
  userId: string;
  userName: string;
  ingredientId: string;
  ingredientName: string;
  ingredientDisplayName: string;
  ingredientDescripion: string;
  ingredientFieldNames: string;
  ingredientFieldDescriptions: string[];
  ingredientOldValues: string[];
  ingredientNewValues: string[];
}

export interface IngredientPricesSettingChangesItem {
  timeStamp: Date;
  changeType: number;
  changeTypeName: string;
  changeSource: string;
  userId: string;
  userName: string;
  price: number;
  priceOld: number;
  priceDate: Date;
  priceDateOld: Date;
  ingredientName: string;
  ingredientType: string;
}

export interface IngredientDryMatterSettingChangesItem {
  timeStamp: Date;
  changeType: number;
  changeTypeName: string;
  changeSource: string;
  userId: string;
  userName: string;
  dryMatter: number;
  dryMatterOld: number;
  dryMatterDate: Date;
  dryMatterDateOld: Date;
  ingredientName: string;
  ingredientType: string;
}

export interface InventoryHistoryItem {
  ingredientId: string;
  ingredientName: string;
  userId: string;
  userName: string;
  timeStamp: string;
  changeSource: string;
  dryMatterPerc: number;
  stock: number;
  stockChange: number;
  dryStock: number;
  dryStockChange: number;
}

export interface PresetParams {
  payload: any;
  reportSettings: ReportSettingsFormValue;
  labelKey: any;
  daysBack: number;
  endDate: Date | string;
  viewSettings: any;
}
